import { useEffect, useState } from 'react';
import { submitMatchScore, updateMatchStatus } from '../../api/tournamentsApi';
import { useToast } from '../Toast';
import { IconCheck } from './TournamentIcons';
import { MemberStack } from '../memberDisplay';

const ScoreModal = ({ match, teamANames, teamBNames, teamAPlayers, teamBPlayers, publicId, tournament, roundNumber, onSaved, onClose, clientId }) => {
  const { addToast } = useToast();
  // Если счёт не введён — подставляем 0:0
  const [scoreA, setScoreA] = useState(match.teamAScore ?? 0);
  const [scoreB, setScoreB] = useState(match.teamBScore ?? 0);
  const [saving, setSaving] = useState(false);
  // Анимация появления сайдбара (слайд снизу). Сначала рендерим за экраном,
  // на следующий кадр поднимаем — лист едет вверх.
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Цифровая клавиатура + выделение текущего значения при фокусе.
  const numberInputProps = {
    type: 'text',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    maxLength: 3,
    onFocus: e => e.target.select(),
  };
  const sanitize = v => v.replace(/[^0-9]/g, '').slice(0, 3);

  const isEmpty = scoreA === '' || scoreB === '';
  const canSave = isEmpty || (
    !isNaN(parseInt(scoreA, 10)) && !isNaN(parseInt(scoreB, 10))
    && parseInt(scoreA, 10) >= 0 && parseInt(scoreB, 10) >= 0
  );

  // M2.2: проверка перед reopen (переход FINISHED → PENDING) для Mexicano.
  // Вызывается когда match.status === 'FINISHED' и пользователь хочет исправить счёт.
  // Возвращает false если нужно прервать операцию (пользователь отказался или заблокировано).
  const checkMexicanoReopen = () => {
    if (tournament?.format !== 'MEXICANO') return true;

    const subsequent = (tournament.rounds || []).filter(r => r.roundNumber > roundNumber);
    if (subsequent.length === 0) return true;

    const hasStarted = subsequent.some(r =>
      (r.matches || []).some(m => m.status !== 'PENDING')
    );

    if (hasStarted) {
      // Следующий тур уже начат — блокируем, не показываем confirm
      addToast(
        'Нельзя исправить счёт: в следующем туре уже сыграны матчи. Сначала очистите их.',
        'error'
      );
      return false;
    }

    // Следующий тур есть, но все матчи PENDING — предупреждаем о пересборке
    return window.confirm(
      'Следующий тур будет пересобран по новому рейтингу после повторного сохранения результата.\n\nПродолжить?'
    );
  };

  // B3.2: проверка перед reopen для Beat the Box.
  // Логика симметрична checkMexicanoReopen, но граница — между этапами (не турами).
  // Туры 1–3 = этап 1, туры 4–6 = этап 2. Реопен матча этапа 2 — без ограничений.
  const checkBeatTheBoxReopen = () => {
    if (tournament?.format !== 'BEAT_THE_BOX') return true;

    // Реопен матча этапа 2 (roundNumber 4–6) — пары не зависят от результатов, разрешаем
    if (roundNumber > 3) return true;

    // Реопен матча этапа 1 — проверяем состояние этапа 2
    const stage2Rounds = (tournament.rounds || []).filter(r => r.roundNumber > 3);
    if (stage2Rounds.length === 0) return true;

    const stage2Started = stage2Rounds.some(r =>
      (r.matches || []).some(m => m.status !== 'PENDING')
    );

    if (stage2Started) {
      // Этап 2 уже начат — блокируем без confirm
      addToast(
        'Нельзя исправить счёт этапа 1: в этапе 2 уже сыграны матчи. Сначала очистите их.',
        'error'
      );
      return false;
    }

    // Этап 2 существует, но все матчи PENDING — предупреждаем о пересборке групп
    return window.confirm(
      'После повторного сохранения результата этапа 1 группы Gold/Silver/… будут пересобраны заново.\n\nПродолжить?'
    );
  };

  // Сохранить счёт — если поля пусты, очищаем счёт на бэкенде (PENDING).
  // Если матч уже FINISHED и вводим новый счёт — сначала сбрасываем в PENDING.
  // version передаётся для optimistic locking; при 409 — данные уже обновлены через SSE.
  const handleSave = async () => {
    if (!canSave) return;

    // Для FINISHED-матча — проверка политики reopen перед API-вызовом
    if (match.status === 'FINISHED' && !checkMexicanoReopen()) return;
    if (match.status === 'FINISHED' && !checkBeatTheBoxReopen()) return;

    setSaving(true);
    try {
      let updatedMatch;
      if (isEmpty) {
        // Удалить счёт: откатываем матч в PENDING (откат статистики)
        updatedMatch = await updateMatchStatus(publicId, match.id, {
          status: 'PENDING',
          version: match.version ?? null,
        }, clientId);
      } else {
        if (match.status === 'FINISHED') {
          await updateMatchStatus(publicId, match.id, {
            status: 'PENDING',
            version: match.version ?? null,
          }, clientId);
        }
        updatedMatch = await submitMatchScore(publicId, match.id, {
          teamAScore: parseInt(scoreA, 10),
          teamBScore: parseInt(scoreB, 10),
          version: match.version ?? null,
        }, clientId);
      }
      onSaved(updatedMatch);
      onClose();
    } catch (err) {
      if (err.status === 409) {
        // Гонка двух организаторов или бэкенд заблокировал reopen.
        // onClose вызовет handleScoreModalClose → там маркер будет сброшен.
        addToast(err.message || 'Матч изменён другим организатором — данные обновлены', 'warning');
        onClose();
      } else {
        // Сетевая/серверная ошибка — модал остаётся открытым, пользователь может повторить.
        // Маркер не сбрасываем: сохранение не дошло до бэкенда, SSE не придёт.
        addToast(`Ошибка: ${err.message}`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: 72, height: 64, textAlign: 'center',
    fontSize: 28, fontWeight: 700,
    border: '2px solid #e5e7eb', borderRadius: 12,
    background: '#f9fafb', color: '#111827',
    outline: 'none',
    WebkitAppearance: 'none', MozAppearance: 'textfield',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 16px',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
          opacity: shown ? 1 : 0, transition: 'opacity 0.2s ease',
        }}
      />

      {/* Карточка */}
      <div style={{
        position: 'relative', width: '100%', maxWidth: 360,
        background: '#fff', borderRadius: 20,
        padding: '24px 20px 20px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        opacity: shown ? 1 : 0,
        transform: shown ? 'scale(1)' : 'scale(0.92)',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        willChange: 'transform, opacity',
      }}>

        {/* Заголовок */}
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: 0.8,
          textTransform: 'uppercase', color: '#9ca3af',
          textAlign: 'center', marginBottom: 16,
        }}>
          КОРТ {match.courtNumber}
        </div>

        {/* Команды и ввод — вертикально: каждая команда своей строкой, её счёт справа.
            Имена получают всю ширину карточки и помещаются без обрезки/тапов. */}
        <div style={{ marginBottom: 20 }}>
          {/* Команда A */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(teamAPlayers && teamAPlayers.length)
                ? teamAPlayers.map((p, i) => (
                    <MemberStack key={i} member={p} fontSize={15} color="#111827" align="left" />
                  ))
                : teamANames.split(' / ').map((n, i) => (
                    <div key={i} style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{n}</div>
                  ))}
            </div>
            <input
              {...numberInputProps}
              value={scoreA}
              onChange={e => setScoreA(sanitize(e.target.value))}
              style={inputStyle}
            />
          </div>

          {/* Разделитель со счётом */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0',
          }}>
            <div style={{ flex: 1, height: 1, background: '#f0f0f5' }} />
            <span style={{
              fontSize: 12, fontWeight: 700, color: '#cbd5e1',
              width: inputStyle.width, textAlign: 'center', flexShrink: 0,
            }}>
              VS
            </span>
          </div>

          {/* Команда B */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(teamBPlayers && teamBPlayers.length)
                ? teamBPlayers.map((p, i) => (
                    <MemberStack key={i} member={p} fontSize={15} color="#111827" align="left" />
                  ))
                : teamBNames.split(' / ').map((n, i) => (
                    <div key={i} style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{n}</div>
                  ))}
            </div>
            <input
              {...numberInputProps}
              value={scoreB}
              onChange={e => setScoreB(sanitize(e.target.value))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Кнопка очистки — сбрасывает поля в UI, без запроса на бэкенд */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            onClick={() => { setScoreA(''); setScoreB(''); }}
            disabled={saving}
            style={{
              background: 'none', border: 'none',
              fontSize: 12, color: '#9ca3af',
              cursor: 'pointer', padding: '2px 8px',
              textDecoration: 'underline', textUnderlineOffset: 2,
            }}
          >
            Очистить
          </button>
        </div>

        {/* Кнопки действий */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, padding: '13px', borderRadius: 12,
              border: '1.5px solid #d1d5db',
              background: '#fff', color: '#374151',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            style={{
              flex: 2, padding: '13px', borderRadius: 12,
              border: 'none',
              background: canSave && !saving
                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                : '#e5e7eb',
              color: canSave && !saving ? '#fff' : '#9ca3af',
              fontSize: 14, fontWeight: 700,
              cursor: canSave && !saving ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.15s',
            }}
          >
            {saving ? 'Сохранение...' : <><IconCheck /> Сохранить счёт</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreModal;
